import React, { FC } from 'react'
import { Route, Switch } from 'react-router-dom'

import {
  useFlag,
  DynamicLoadingPage,
  NotFoundView,
  routes,
} from '@sketch/modules-common'

import {
  useBellNotifications,
  useVersionSubscriptions,
  useProjectMembershipSubscriptions,
  PrivateRoute,
  UserAcceptedToS,
  AcceptShareInviteByInviteTokenView,
  AcceptShareInviteByShareShortIdView,
  ShareRoutes,
  DocPage,
  PersonalAuthRoute,
  PrivateWorkspaceRoute,
  WorkspaceSettingsView,
  WorkspaceSubscribeView,
  WorkspacePartnerSubscribeView,
  RecoveryCodeView,
  UserToWorkspaceRedirect,
  VerificationCodeView,
  WorkspaceProfilePublicView,
  PublicationView,
  AcceptProjectInviteByTokenView,
  ShareSubscriptions,
  RestrictedWorkspaceBannerManagerSwitch,
  CommunityView,
  CommunityListingView,
} from 'cloud-frontend'

import MFAPrivateRoute from '../../containers/MFAPrivateRoute'
import {
  WorkspaceSidebarLayoutRoutes,
  workspaceSidebarLayoutRoutePaths,
} from '../../containers/WorkspaceSidebarLayoutRoutes'

// Styles
import { CoreLayoutWrapper } from './CoreLayout.styles'

const AcceptWorkspaceInviteView = React.lazy(
  () =>
    import(
      'cloud-frontend/dist/esm/modules/workspace/views/AcceptWorkspaceInviteView'
    )
)

const CreateWorkspaceView = React.lazy(
  () =>
    import(
      'cloud-frontend/dist/esm/modules/workspace/views/CreateWorkspaceView'
    )
)

const CreateWorkspaceDoneView = React.lazy(
  () =>
    import(
      'cloud-frontend/dist/esm/modules/workspace/views/CreateWorkspaceDoneView'
    )
)

const CoreLayout: FC = () => {
  useBellNotifications()
  useVersionSubscriptions()
  useProjectMembershipSubscriptions()

  const isDesignSystemsOn = useFlag('design-systems')
  const isCommunityCanvasOn = useFlag('community-canvas')

  return (
    <CoreLayoutWrapper>
      <ShareSubscriptions />
      <React.Suspense fallback={<DynamicLoadingPage />}>
        <RestrictedWorkspaceBannerManagerSwitch />
        <Switch>
          {/* Redirect from Baby Cloud to the Personal-Workspace */}
          <PrivateRoute
            path={[
              routes.PERSONAL_PROJECT.template(),
              routes.DOCUMENTS.template(),
              routes.LIBRARIES.template(),
              routes.TRASH.template(),
              routes.PERSONAL_DOCUMENTS.template(),
            ]}
            component={UserToWorkspaceRedirect}
          />

          {/* 2FA Routes */}
          <MFAPrivateRoute
            path={routes.VERIFICATION_CODE.template()}
            component={VerificationCodeView}
          />

          <MFAPrivateRoute
            path={routes.RECOVERY_CODE.template()}
            component={RecoveryCodeView}
          />

          {/* Workspace Routes */}
          <PrivateRoute
            path={routes.WORKSPACE_INVITE.template()}
            component={AcceptWorkspaceInviteView}
          />

          <PersonalAuthRoute
            fallbackRouteIfUserOnlyHasSsoAuth={({ location }) => ({
              pathname: routes.SIGN_IN.create({}),
              state: { from: location },
            })}
            path={[
              routes.WORKSPACE_CREATE.template(),
              routes.WORKSPACE_CREATE_DONE.template(),
            ]}
            render={() => (
              <>
                <PrivateRoute
                  exact
                  path={routes.WORKSPACE_CREATE_DONE.template()}
                  render={({ match }) => (
                    <CreateWorkspaceDoneView
                      workspaceId={match.params.workspaceId!}
                    />
                  )}
                />
                <PrivateRoute
                  exact
                  path={routes.WORKSPACE_CREATE.template()}
                  redirectPathIfSignOut="sign-up"
                  component={CreateWorkspaceView}
                />
              </>
            )}
          />

          <Route
            exact
            path={workspaceSidebarLayoutRoutePaths}
            component={WorkspaceSidebarLayoutRoutes}
          />

          <PrivateWorkspaceRoute
            path={routes.WORKSPACE_SETTINGS.template()}
            component={WorkspaceSettingsView}
          />

          <PrivateWorkspaceRoute
            path={routes.WORKSPACE_SUBSCRIBE.template()}
            component={WorkspaceSubscribeView}
          />

          <PrivateRoute
            exact
            path={routes.WORKSPACE_SUBSCRIBE_PARTNER.template()}
            component={WorkspacePartnerSubscribeView}
          />

          {isCommunityCanvasOn && (
            <Route
              exact
              path={routes.COMMUNITY_CANVAS.template()}
              component={CommunityView}
            />
          )}

          {isCommunityCanvasOn && (
            <Route
              exact
              path={routes.COMMUNITY_CANVAS_LISTING.template()}
              component={CommunityListingView}
            />
          )}

          <Route
            exact
            path={routes.WORKSPACE_PROFILE.template()}
            component={WorkspaceProfilePublicView}
          />

          <Route
            exact
            path={routes.WORKSPACE_PROFILE_DOCUMENT.template()}
            component={PublicationView}
          />

          {isDesignSystemsOn && (
            <PrivateWorkspaceRoute
              exact
              path={routes.DESIGN_SYSTEM_PAGES.template()}
              component={DocPage}
            />
          )}

          {/* Shares */}
          <Route
            path={routes.SHARE_INVITE.template()}
            component={AcceptShareInviteByShareShortIdView}
          />

          <Route
            path={routes.SHARE_ACCEPT_INVITE_ROOT.template()}
            component={AcceptShareInviteByInviteTokenView}
          />

          <Route path={routes.SHARE_VIEW.template()} component={ShareRoutes} />

          {/* Projects */}
          <Route
            path={routes.PROJECT_ACCEPT_INVITE.template()}
            component={AcceptProjectInviteByTokenView}
          />

          {/* Not found default */}
          <Route component={NotFoundView} />
        </Switch>
        <UserAcceptedToS />
      </React.Suspense>
    </CoreLayoutWrapper>
  )
}

export default CoreLayout
